/**
 * @fileoverview AppRouter — Lazy loading + Suspense + ErrorBoundary routing.
 * react-router-dom v7; public site MainLayout; /admin rotaları ayrı (layout yok).
 */

import { lazy, Suspense } from 'react'
import {
  Navigate,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  useParams,
} from 'react-router-dom'

import MainLayout         from '../layouts/MainLayout'
import Spinner            from '../components/common/Spinner'
import ErrorBoundary      from '../errors/ErrorBoundary'
import RouteErrorBoundary from '../errors/RouteErrorBoundary'

function PortfolioReferenceEditRedirect() {
  const { id } = useParams()
  const n = Number.parseInt(String(id ?? ''), 10)
  if (!Number.isFinite(n) || n < 1) {
    return <Navigate to="/admin/referanslarim" replace />
  }
  return <Navigate to="/admin/referanslarim" replace state={{ editReferenceId: n }} />
}

// ─── Lazy sayfalar (panel ayrı chunk — react-icons/md|fa6|ri, formik, dnd-kit vb. genel bundle’a girmez)
const Home            = lazy(() => import('../pages/Home'))
const About           = lazy(() => import('../pages/About'))
const Contact         = lazy(() => import('../pages/Contact'))
const NotFound        = lazy(() => import('../pages/NotFound'))
const PortfolioPage   = lazy(() => import('../features/portfolio/PortfolioPage'))
const PortfolioDetail = lazy(() => import('../features/portfolio/PortfolioDetail'))

const AdminLogin = lazy(() => import('../features/admin/pages/AdminLogin'))
const AdminLayout = lazy(() => import('../features/admin/layouts/AdminLayout'))
const AdminDashboardPage = lazy(() => import('../features/admin/pages/AdminDashboardPage'))
const AdminLanguagesPage = lazy(() => import('../features/admin/pages/AdminLanguagesPage'))
const AdminCategoriesPage = lazy(() => import('../features/admin/pages/AdminCategoriesPage'))
const AdminGalleryPage = lazy(() => import('../features/admin/pages/AdminGalleryPage'))
const AdminHomePageSettingsPage = lazy(() => import('../features/admin/pages/AdminHomePageSettingsPage'))
const AdminAboutPageSettingsPage = lazy(() => import('../features/admin/pages/AdminAboutPageSettingsPage'))
const AdminPortfolioPageSettingsPage = lazy(() =>
  import('../features/admin/pages/AdminPortfolioPageSettingsPage')
)
const AdminContactPageSettingsPage = lazy(() => import('../features/admin/pages/AdminContactPageSettingsPage'))
const AdminSiteLayoutSettingsPage = lazy(() => import('../features/admin/pages/AdminSiteLayoutSettingsPage'))
const AdminPortfolioReferencesListPage = lazy(() =>
  import('../features/admin/pages/AdminPortfolioReferencesListPage')
)
const AdminProfilePage = lazy(() => import('../features/admin/pages/AdminProfilePage'))

/** Full-screen Suspense fallback */
const PageSpinner = () => <Spinner fullScreen />

/** @param {React.LazyExoticComponent<React.ComponentType>} LazyComponent */
function withSuspense(LazyComponent) {
  const LazyPage = LazyComponent
  return function LazyRouteBoundary() {
    return (
      <Suspense fallback={<PageSpinner />}>
        <LazyPage />
      </Suspense>
    )
  }
}

const HomeBoundary = withSuspense(Home)
const AboutBoundary = withSuspense(About)
const ContactBoundary = withSuspense(Contact)
const NotFoundBoundary = withSuspense(NotFound)
const PortfolioPageBoundary = withSuspense(PortfolioPage)
const PortfolioDetailBoundary = withSuspense(PortfolioDetail)

const AdminLoginBoundary = withSuspense(AdminLogin)
const AdminLayoutBoundary = withSuspense(AdminLayout)
const AdminDashboardBoundary = withSuspense(AdminDashboardPage)
const AdminLanguagesBoundary = withSuspense(AdminLanguagesPage)
const AdminCategoriesBoundary = withSuspense(AdminCategoriesPage)
const AdminGalleryBoundary = withSuspense(AdminGalleryPage)
const AdminHomeSettingsBoundary = withSuspense(AdminHomePageSettingsPage)
const AdminAboutSettingsBoundary = withSuspense(AdminAboutPageSettingsPage)
const AdminPortfolioSettingsBoundary = withSuspense(AdminPortfolioPageSettingsPage)
const AdminContactSettingsBoundary = withSuspense(AdminContactPageSettingsPage)
const AdminSiteLayoutSettingsBoundary = withSuspense(AdminSiteLayoutSettingsPage)
const AdminPortfolioReferencesBoundary = withSuspense(AdminPortfolioReferencesListPage)
const AdminProfileBoundary = withSuspense(AdminProfilePage)

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route
        path="/admin/login"
        element={<AdminLoginBoundary />}
        errorElement={<RouteErrorBoundary />}
      />
      <Route
        path="/admin"
        element={<AdminLayoutBoundary />}
        errorElement={<RouteErrorBoundary />}
      >
        <Route index element={<AdminDashboardBoundary />} />
        <Route path="profil" element={<AdminProfileBoundary />} />
        <Route path="tanimlamalar/diller" element={<AdminLanguagesBoundary />} />
        <Route path="tanimlamalar/kategoriler" element={<AdminCategoriesBoundary />} />
        <Route path="tanimlamalar/galeri" element={<AdminGalleryBoundary />} />
        <Route
          path="site-yapisi"
          element={<Navigate to="/admin/site-yapisi/header-footer" replace />}
        />
        <Route path="site-yapisi/header-footer" element={<AdminSiteLayoutSettingsBoundary />} />
        <Route
          path="sayfa-ayarlari"
          element={<Navigate to="/admin/sayfa-ayarlari/ana-sayfa" replace />}
        />
        <Route path="sayfa-ayarlari/ana-sayfa" element={<AdminHomeSettingsBoundary />} />
        <Route path="sayfa-ayarlari/hakkimizda" element={<AdminAboutSettingsBoundary />} />
        <Route path="sayfa-ayarlari/referanslar" element={<AdminPortfolioSettingsBoundary />} />
        <Route path="sayfa-ayarlari/iletisim" element={<AdminContactSettingsBoundary />} />
        <Route
          path="referanslarim/yeni"
          element={<Navigate to="/admin/referanslarim" replace state={{ openNewReference: true }} />}
        />
        <Route path="referanslarim/:id" element={<PortfolioReferenceEditRedirect />} />
        <Route path="referanslarim" element={<AdminPortfolioReferencesBoundary />} />
      </Route>
      <Route
        path="/"
        element={<MainLayout />}
        errorElement={<RouteErrorBoundary />}
      >
        <Route index element={<HomeBoundary />} />
        <Route path="about" element={<AboutBoundary />} />
        <Route path="contact" element={<ContactBoundary />} />
        <Route path="portfolio" element={<PortfolioPageBoundary />} />
        <Route path="portfolio/:id" element={<PortfolioDetailBoundary />} />
        <Route path="*" element={<NotFoundBoundary />} />
      </Route>
    </>
  )
)

export default function AppRouter() {
  return (
    <ErrorBoundary>
      <RouterProvider router={router} fallbackElement={<PageSpinner />} />
    </ErrorBoundary>
  )
}
